import { useActiveLibraryId } from '@lib/hooks/useActiveLibraryId';
import { getArtifactsUrl, getChatsUrl, getNewChatUrl, openAuthedChatUrl } from '@lib/network/chats';
import { trackingStore } from '@stores/Tracking';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

export function useNavActions() {
  const nav = useNavigate();
  const activeLibraryId = useActiveLibraryId();

  const navigateToSearch = useCallback(() => {
    trackingStore.navItemClicked('Search', '/search');
    nav(activeLibraryId ? `/search?libs=${activeLibraryId}` : '/search');
  }, [nav, activeLibraryId]);

  // New Chat carries the in-context library, silently. The chat app reads
  // `?library=` from the new-chat URL.
  const navigateToNewChat = useCallback(() => {
    trackingStore.navItemClicked('New Chat', '/chat/new');
    void openAuthedChatUrl(getNewChatUrl(activeLibraryId ?? undefined), '_self', {
      surface: 'sidebar',
      control: 'new-chat-button',
      libraryId: activeLibraryId ?? undefined,
    });
  }, [activeLibraryId]);

  // Chats lands on the chat app's list/search surface. No library scoping —
  // it's a utility list, not a new-chat action.
  const navigateToChats = useCallback(() => {
    trackingStore.navItemClicked('Chats', '/chats');
    void openAuthedChatUrl(getChatsUrl(), '_self', {
      surface: 'sidebar',
      control: 'chats-button',
    });
  }, []);

  // Artifacts lands on the chat app's account-wide artifacts page. Like Chats,
  // it's a flat utility surface — no library scoping.
  const navigateToArtifacts = useCallback(() => {
    trackingStore.navItemClicked('Artifacts', '/artifacts');
    void openAuthedChatUrl(getArtifactsUrl(), '_self', {
      surface: 'sidebar',
      control: 'artifacts-button',
    });
  }, []);

  return {
    navigateToSearch,
    navigateToNewChat,
    navigateToChats,
    navigateToArtifacts,
  };
}
